import React, { useState, useEffect } from 'react';
import { Card, CardPreview, CardHeader, Text, Link, Caption1, Button, makeStyles } from '@fluentui/react-components';
import { MoreHorizontal20Regular } from '@fluentui/react-icons';
// import styles from './HomePageCards.module.css'; // adjust your styles as needed

// Use require.context to load images from the assets folder.
const imagesContext = require.context('../Assets/cards', false, /\.(png|jpe?g|svg)$/);
const cardImages = imagesContext.keys().map((file) => file.replace('./', ''));

// Helper for importing images (fallback to a default if not found)
function importImage(imageName, defaultName) {
  try {
    return require(`../Assets/cards/${imageName}`);
  } catch (error) {
    return require(`../Assets/cards/${defaultName}`);
  }
}

const useStyles = makeStyles({
  card: {
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: '16px',
    margin: '16px',
    backgroundColor: '#ffffff',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  grayBackground: {
    backgroundColor: '#f3f2f1',
    padding: '8px',
    textAlign: 'center',
  },
  logoBadge: {
    width: '50px',
    height: '50px',
    objectFit: 'contain',
    marginBottom: '8px',
  },
  smallRadius: {
    borderRadius: '4px',
    maxWidth: '100%',
    height: 'auto',
  },
  link: {
    textDecoration: 'none',
    color: 'inherit',
  },
  caption: {
    color: '#605e5c',
    fontSize: '14px',
  },
});

function HomePageCards() {
  const styles = useStyles();
  const [cards, setCards] = useState([]);
  const [editingCardId, setEditingCardId] = useState(null);
  const [formData, setFormData] = useState({
    header: '',
    description: '',
    image: '',
    link: '',
    newwindow: false,
    isActive: true,
    // Optional logo fields (if needed)
    logo: '',
    logoalt: '',
    imagealt: '',
  });

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = () => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/homepage-cards`)
      .then((res) => res.json())
      .then((data) => setCards(data))
      .catch((err) => console.error('Failed to fetch homepage cards:', err));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const method = editingCardId ? 'PUT' : 'POST';
    const url = editingCardId
      ? `${process.env.REACT_APP_BACKEND_URL}/api/homepage-cards/${editingCardId}`
      : `${process.env.REACT_APP_BACKEND_URL}/api/homepage-cards`;

    fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    })
      .then(() => {
        fetchCards();
        setFormData({
          header: '',
          description: '',
          image: '',
          link: '',
          newwindow: false,
          isActive: true,
          logo: '',
          logoalt: '',
          imagealt: '',
        });
        setEditingCardId(null);
      })
      .catch((err) => console.error(`Error ${editingCardId ? 'updating' : 'adding'} homepage card:`, err));
  };

  const handleDelete = (id) => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/homepage-cards/${id}`, {
      method: 'DELETE',
    })
      .then(() => fetchCards())
      .catch((err) => console.error('Error deleting homepage card:', err));
  };

  const handleEdit = (card) => {
    setEditingCardId(card.id);
    setFormData({
      header: card.header,
      description: card.description,
      image: card.image,
      link: card.link,
      newwindow: card.newwindow,
      isActive: card.isActive,
      logo: card.logo || '',
      logoalt: card.logoalt || '',
      imagealt: card.imagealt || '',
    });
  };

  return (
    <div>
      <h2>Manage Homepage Cards</h2>
      <ul>
        {cards.map((card) => (
          <li key={card.id}>
            <strong>{card.header}</strong> - {card.description}
            <button onClick={() => handleEdit(card)}>Edit</button>
            <button
              onClick={() => handleDelete(card.id)}
              style={{ marginLeft: '10px', backgroundColor: 'red', color: 'white' }}
            >
              Delete
            </button>
          </li>
        ))}
      </ul>

      <h3>{editingCardId ? 'Edit Card' : 'Add New Card'}</h3>
      <div style={{ display: 'flex', gap: '20px' }}>
        {/* Form Section */}
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            name="header"
            value={formData.header}
            onChange={handleChange}
            placeholder="Header"
            required
          />
          <input
            type="text"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Description"
          />

          {/* Dropdown for image selection */}
          <select name="image" value={formData.image} onChange={handleChange} required>
            <option value="">Select an image</option>
            {cardImages.map((img) => (
              <option key={img} value={img}>
                {img}
              </option>
            ))}
          </select>

          <input
            type="text"
            name="link"
            value={formData.link}
            onChange={handleChange}
            placeholder="Link URL"
          />
          <label>
            Open in new window:
            <input
              type="checkbox"
              name="newwindow"
              checked={formData.newwindow}
              onChange={handleChange}
            />
          </label>
          <label>
            Active:
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
            />
          </label>
          <button type="submit">{editingCardId ? 'Update Card' : 'Add Card'}</button>
          {editingCardId && (
            <button type="button" onClick={() => setEditingCardId(null)}>
              Cancel
            </button>
          )}
        </form>

        {/* Preview Section */}
        <div>
          <h3>Preview</h3>
          <Card className={styles.card} focusMode="off">
            <CardPreview className={styles.grayBackground}
              logo={
                <img
                  className={styles.logoBadge}
                  src={importImage(formData.logo || 'carddefaultlogo.png', 'carddefaultlogo.png')}
                  alt={formData.logoalt || 'Card Logo'}
                />
              }
            >
              <img
                className={styles.smallRadius}
                src={importImage(formData.image || 'carddefaultimage.png', 'carddefaultimage.png')}
                alt={formData.imagealt || 'Card Image'}
              />
            </CardPreview>
            <CardHeader
              header={
                <Link
                  to={formData.link || '#'}
                  target={formData.newwindow ? '_blank' : undefined}
                  className={styles.link}
                >
                  <Text weight="semibold">
                    {formData.header || 'Default Header'}
                  </Text>
                </Link>
              }
              description={
                <Caption1 className={styles.caption}>
                  {formData.description || 'Default Description'}
                </Caption1>
              }
              action={
                <Button
                  appearance="transparent"
                  icon={<MoreHorizontal20Regular />}
                  aria-label="More actions"
                />
              }
            />
          </Card>
        </div>
      </div>
    </div>
  );
}

export default HomePageCards;
